<script>
  import { onMount, tick, createEventDispatcher } from "svelte";
  import upperFirst from "lodash/upperFirst";
  import uniq from "lodash/uniq";
  import set from "lodash/set";
  import { tooltip } from "svelte-utilities";
  import { degToRad, distSq } from "vector";
  import { transformPt } from "drawing";

  import { Sentry } from "#src/glitchtip.js";
  import XICon from "@local/assets/icons/x.svg";
  import CircleXIcon from "@local/assets/icons/circle-x-filled.svg";
  import CaretLeftIcon from "@local/assets/icons/caret-left.svg";
  import CaretRightIcon from "@local/assets/icons/caret-right.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import QuestionAsteriskIcon from "@local/assets/icons/question-asterisk.svg";
  import SelectedIcon from "@local/assets/icons/selected.svg";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import InvertIcon from "@local/assets/icons/invert.svg";
  import BlankIcon from "@local/assets/icons/blank.svg";
  import SearchIcon from "@local/assets/icons/search.svg";
  import ZoomFitIcon from "@local/assets/icons/zoom-fit.svg";
  import IsolateIcon from "@local/assets/icons/temp-isolate.svg";
  import eb from "#src/extensions/event-bus.js";
  import {
    useWildcardsInSearch,
    useSelectedInSearch,
    showSearchResults,
    showSearchedMarkersByDefault,
    selectedPages,
    hiddenPages,
    documentViewCollectionFilter,
    documentViewItemFilter,
    documentViewTypeFilter,
    documentViewNoteFilter,
    documentViewLocationFilter,
  } from "#src/stores/ui.js";
  import { user } from "#src/stores/auth.js";
  import {
    escapeSpecialChars,
    escapeWithWildcards,
    escapeRegExp,
  } from "#src/extensions/sanitize-postgrest-string.js";
  import { transformSheetPoint } from "@local/extensions/geometry/transform-sheet-points.js";
  import { createItem, createType, createNote, createLocation } from "@local/lamina-core";
  import { api } from "#src/api/";

  export let docid;
  export let group;
  export let items;
  export let collections;
  export let types;
  export let locations;
  export let documentNotes;
  export let visibleIndices;
  export let highlightedText;
  export let searchResults;
  export let importSearchResultsAs;

  const dispatch = createEventDispatcher();

  const recordName = {
    item: "item",
    collection: "opening",
    type: "type",
    document_note: "note",
  };

  let visible = false;
  let input;
  let allResults = [];
  let currentResult = 0;
  let searchText = "";
  let sentSearchText = "";
  let selected = false;
  let searching = false;
  let showImportAs = false;
  let searchForLocationsOf = "item";
  let showLocationPanel = false;
  let overallRecordSelection;
  let selectedRecords = {};
  let recordSelector;
  let searchType = "text";
  let initialToggle = true;

  $: results = filterVisible(allResults, visibleIndices);
  $: hiddenResults = allResults.filter((r) => visibleIndices[r?.page_id] === undefined);
  $: filtered = filterResults(results, $useSelectedInSearch, $selectedPages, $hiddenPages);
  $: checkResult(currentResult, filtered, highlightedText);
  $: current = filtered[currentResult];
  $: resetResults(searchText);
  $: canShowResults =
    visible &&
    !searching &&
    filtered.length &&
    (searchText === sentSearchText || searchType === "location") &&
    !!current;
  $: updateHighlightedText(searchText, canShowResults, searchResults);
  $: updateSelected($useSelectedInSearch);
  $: recordsToSearch = getRecordsToSearch(searchForLocationsOf);
  $: selectedSearchedRecords = recordsToSearch.filter((r) => selectedRecords[r.id]);
  $: updateRecordsToSearch(searchForLocationsOf);
  $: checkSelectionState(recordsToSearch, selectedRecords);

  function filterVisible(results, visibleIndices) {
    return results
      .filter((r) => visibleIndices[r.page_id] !== undefined)
      .filter((res, i, arr) => {
        const prev = arr[i - 1];
        const overlaps = prev
          ? distSq(res.insertion_point, prev.insertion_point) < 10 && res.content === prev.content
          : false;

        return !overlaps;
      });
  }

  function resetResults(searchText) {
    searchResults = [];
  }

  function updateSelected(useselected) {
    if (selected && current?.page_id !== highlightedText?.page_id) {
      currentResult = 0;
      if (filtered[currentResult]) {
        select(filtered[currentResult]);
      }
    }
  }

  function filterResults(results, useSelected, sp, hp) {
    if (!useSelected) return results;
    if (Object.keys(sp).length === 0) return results;

    return results.filter((r) => sp[r.page_id]);
  }

  function checkResult(currentResult, results, ht) {
    if (!results[currentResult]) {
      currentResult = 0;

      if (ht) {
        highlightedText = null;
      }
    }
  }

  // Assuming 300 DPI and 72 pts/inch for PDF rasterization
  const sf = 300 / 72;

  function tileSize(w, h, sr) {
    if ([90, 270].includes(sr)) {
      return {
        x: h * sf,
        y: w * sf,
      };
    }

    return {
      x: w * sf,
      y: h * sf,
    };
  }

  function tilePoint(p, sheetLimits, sheetRotation) {
    const w = sheetLimits[1][0] - sheetLimits[0][0];
    const h = sheetLimits[1][1] - sheetLimits[0][1];
    const ts = tileSize(w, h, sheetRotation);

    const initPt = {
      x: p.x - sheetLimits[0][0],
      y: p.y - sheetLimits[0][1],
    };

    return transformSheetPoint(initPt, sheetRotation, sf, ts);
  }

  async function toggleVisible() {
    eb.dispatch("close-doc-panels", "search");

    visible = !visible;
    showLocationPanel = false;
    selectedRecords = recordsToSearch.reduce((m, r) => {
      m[r.id] = true;
      return m;
    }, {});

    await tick();
    if (visible && input) {
      input.focus();
      if (filtered[currentResult]) {
        select(filtered[currentResult]);
      }
      $showSearchResults = true;
    } else {
      highlightedText = null;
      $showSearchResults = false;
    }
  }

  async function closePanel(except) {
    if (except === "search") return;
    visible = false;
    highlightedText = null;
    $showSearchResults = false;
  }

  function toggleItemSearch() {
    showLocationPanel = !showLocationPanel;
  }

  function updateRecordsToSearch(searchForLocationsOf) {
    selectedRecords = recordsToSearch.reduce((m, r) => {
      m[r.id] = true;
      return m;
    }, {});
  }

  function checkSelectionState(recordsToSearch, selectedRecords) {
    if (recordsToSearch.every((r) => selectedRecords[r.id])) {
      overallRecordSelection = true;
    } else if (recordsToSearch.every((r) => !selectedRecords[r.id])) {
      overallRecordSelection = false;
    } else {
      overallRecordSelection = "mixed";
    }

    if (recordSelector) {
      if (overallRecordSelection === "mixed") {
        recordSelector.indeterminate = true;
      } else {
        recordSelector.indeterminate = false;
      }
    }
  }

  function selectAllRecords() {
    if (recordsToSearch.some((r) => selectedRecords[r.id])) {
      selectedRecords = {};
    } else {
      selectedRecords = recordsToSearch.reduce((m, r) => {
        m[r.id] = true;
        return m;
      }, {});
    }
  }

  function invertRecordSelection() {
    const selected = recordsToSearch.filter((r) => !selectedRecords[r.id]);
    selectedRecords = selected.reduce((m, r) => {
      m[r.id] = true;
      return m;
    }, {});
  }

  function getRecordsToSearch(searchForLocationsOf) {
    return (
      {
        item: items,
        collection: collections,
        type: types,
        document_note: documentNotes,
      }[searchForLocationsOf] || []
    );
  }

  async function searchForLocations() {
    searchText = "";
    searching = true;
    const queries = recordsToSearch.filter((r) => selectedRecords[r.id]).map((r) => r.mark.trim());
    const res = await api.rpc("search_dng_text_objects_multiple", { document_id: docid, queries });
    if (res.error) {
      const err = new Error(`Error searching for locations in document ${docid}`, {
        cause: res.error,
      });
      Sentry.captureException(err);
    } else {
      allResults = res.data;
    }

    searching = false;
    searchType = "location";
    if ($showSearchedMarkersByDefault) {
      await tick();
      previewResults();
    }
  }

  async function fetchResults(text) {
    if (!text) return;
    sentSearchText = text;
    const t = text;
    const trimmed = text.trim();
    let query = $useWildcardsInSearch ? escapeWithWildcards(trimmed) : escapeSpecialChars(trimmed);

    // Allow use of ^ and $ characters to denote beginning and end of string
    if ($useWildcardsInSearch) {
      if (query.match(/^\^/)) {
        query = query.replace(/^\^/, "");
      } else {
        query = `%${query}`;
      }

      if (query.match(/\$$/)) {
        query = query.replace(/\$$/, "");
      } else {
        query = `${query}%`;
      }
    } else {
      query = `%${query}%`;
    }

    if (!query) return;
    const res = await api.rpc("search_dng_text_objects_ilike", { document_id: docid, query });

    // const words = sanitized.trim().split(/\s+/);
    // if (!words.length) return;
    // const last = words[words.length - 1];
    // words[words.length - 1] = last + ":*";
    // const query = words.join(" & ");

    // const res = await api.rpc("search_dng_text_objects", { document_id: docid, query });

    if (res.error) {
      const err = new Error(`Error searching for text in document ${docid}: ${query}`, {
        cause: res.error,
      });
      Sentry.captureException(err);
    } else if (t === searchText) {
      allResults = res.data;
    }
    searching = false;
  }

  async function search(text) {
    searchType = "text";
    showLocationPanel = false;
    searching = true;
    allResults = [];

    await fetchResults(text);
    if ($showSearchedMarkersByDefault) {
      previewResults();
    }
  }

  function highlightFiltered(results) {
    const ht = {
      highlighted: null,
    };
    results.forEach((result) => {
      const { insertion_point, sheet_rotation, sheet_limits, width, height, angle, page_id } = result;
      const pt = tilePoint(insertion_point, sheet_limits, sheet_rotation);
      const a = degToRad(sheet_rotation - angle);

      if (!ht[page_id]) {
        ht[page_id] = [];
      }

      ht[page_id].push({
        page_id,
        angle: a,
        width: width * sf,
        height: height * sf,
        ...pt,
      });
    });

    highlightedText = ht;
  }

  function select(result) {
    const { insertion_point, sheet_rotation, sheet_limits, width, height, angle, page_id } = result;
    const pt = tilePoint(insertion_point, sheet_limits, sheet_rotation);
    const a = degToRad(sheet_rotation - angle);

    if (!searchResults.length) {
      if (!highlightedText) {
        highlightedText = { highlighted: null };
      }

      highlightedText.highlighted = {
        page_id,
        angle: a,
        width: width * sf,
        height: height * sf,
        ...pt,
      };
    }
  }

  async function chooseSearchText(text) {
    await search(text);
    if (filtered.length) {
      selected = true;
      currentResult = 0;
    }

    await tick();

    highlightFiltered(filtered);
    if (current) {
      select(current);
      moveToPoint(current);
    }
  }

  function handleInput(e) {}

  function rotatePoint(pt, rotation = 0) {
    const rot = degToRad(rotation % 360);
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    return transformPt(pt, [cos, sin, -sin, cos, 0, 0]);
  }

  function resultCenterPoint(result) {
    const { insertion_point, sheet_limits, angle, sheet_rotation, width, height } = result;

    const pos = tilePoint(insertion_point, sheet_limits, sheet_rotation);
    const ctr = { x: (width / 2) * sf, y: -(height / 2) * sf };
    const rotated = rotatePoint(ctr, sheet_rotation - angle);
    const point = {
      x: pos.x + rotated.x,
      y: pos.y + rotated.y,
    };

    return point;
  }

  function moveToPoint(current) {
    const point = resultCenterPoint(current);
    dispatch("move-to-location", { page_id: current.page_id, point });
  }

  async function keydown(e) {
    if (e.key === "Enter" && e.target.value?.length) {
      await search(e.target.value);
      if (filtered.length) {
        selected = true;
        currentResult = 0;
      }

      await tick();

      if (current) {
        select(current);
        moveToPoint(current);
      }
    } else if (e.key === "Escape") {
      toggleVisible();
    }
  }

  function updateHighlightedText(searchText, canShowResults, searchResults) {
    if (canShowResults && !searchResults.length) {
      highlightFiltered(filtered);
    } else {
      highlightedText = null;
    }
  }

  async function prevResult() {
    if (currentResult > 0) {
      currentResult--;
    } else {
      currentResult = filtered.length - 1;
    }

    await tick();
    if (current) {
      select(current);
      moveToPoint(current);
    }
  }

  async function nextResult() {
    if (currentResult < filtered.length - 1) {
      currentResult++;
    } else {
      currentResult = 0;
    }

    await tick();
    if (current) {
      select(current);
      moveToPoint(current);
    }
  }

  function previewResults() {
    $showSearchedMarkersByDefault = true;
    const rawst = searchText.trim();
    const escaped = escapeRegExp(rawst);
    const replaced = $useWildcardsInSearch
      ? escaped.replace(/\\\?/g, ".").replace(/\\\*/g, ".*").replace(/^\\\^/, "").replace(/\\\$$/, "")
      : escaped;

    const re = new RegExp(replaced, "i");

    searchResults = filtered.map((result) => {
      const center = resultCenterPoint(result);
      const id = crypto.randomUUID();

      let label = "";
      if (searchType === "text") {
        const match = result.content.match(re);
        label = match?.[0] ?? "";
      } else if (searchType === "location") {
        label = result.content.trim();
      }

      return {
        id,
        label,
        center,
        page_id: result.page_id,
      };
    });

    highlightedText = null;
  }

  function hidePreview() {
    $showSearchedMarkersByDefault = false;
    searchResults = [];
    highlightFiltered(filtered);
  }

  function importResults() {
    if (!searchResults.length) return;

    let updates = [];

    let e;
    if (importSearchResultsAs === "type") {
      e = types;
    } else if (importSearchResultsAs === "item") {
      e = items;
    } else {
      e = collections;
    }

    const existingMarks = e.reduce((m, t) => {
      m[t.mark] = t.id;
      return m;
    }, {});

    const uniqueMarks = uniq(searchResults.map((r) => r.label));
    let recordIds = uniqueMarks.map((_) => crypto.randomUUID());
    const markMap = uniqueMarks.reduce((m, mark, i) => {
      if (existingMarks[mark]) {
        m[mark] = existingMarks[mark];
      } else {
        m[mark] = recordIds[i];
      }
      return m;
    }, {});
    const newMarks = uniqueMarks.filter((m) => !existingMarks[m]);

    const idMap = searchResults.reduce((m, r) => {
      m[r.id] = markMap[r.label];
      return m;
    }, {});

    // Create types/items
    if (importSearchResultsAs === "type") {
      const newTypes = newMarks.map((mark, i) => {
        const type = createType($group.id);
        type.mark = mark;
        type.id = recordIds[i];
        return type;
      });

      if ($documentViewTypeFilter?.operator === "is_equal_to") {
        newTypes.forEach((type) => {
          set(type, $documentViewTypeFilter.field, $documentViewTypeFilter.value);
        });
      }

      updates.push({ type: "type", action: "add", records: newTypes, block: true });
    } else if (importSearchResultsAs === "document_note") {
      const newNotes = newMarks.map((mark, i) => {
        const note = createNote($group.id, documentNotes);
        note.mark = mark;
        note.id = recordIds[i];
        return note;
      });

      if ($documentViewNoteFilter?.operator === "is_equal_to") {
        newNotes.forEach((note) => {
          set(note, $documentViewNoteFilter.field, $documentViewNoteFilter.value);
        });
      }

      updates.push({ type: "document_note", action: "add", records: newNotes, block: true });
    } else {
      const is_collection = importSearchResultsAs === "collection";
      const newItems = newMarks.map((mark, i) => {
        const item = createItem($group.id, $user?.id, [], [], { is_collection });
        item.mark = mark;
        item.id = recordIds[i];
        return item;
      });

      if (is_collection && $documentViewCollectionFilter?.operator === "is_equal_to") {
        newItems.forEach((item) => {
          set(item, $documentViewCollectionFilter.field, $documentViewCollectionFilter.value);
        });
      } else if (!is_collection && $documentViewItemFilter?.operator === "is_equal_to") {
        newItems.forEach((item) => {
          set(item, $documentViewItemFilter.field, $documentViewItemFilter.value);
        });
      }

      updates.push({ type: "item", action: "add", records: newItems, block: true });
    }

    const locs = searchResults.map((r, i) => {
      return createLocation(
        {
          id: r.id,
          group_id: $group.id,
          document_id: docid,
          index: locations.length + i,
          type: "pin",
          position: r.center,
          page_id: r.page_id,
          record_type: importSearchResultsAs,
          record_id: idMap[r.id],
        },
        $documentViewLocationFilter,
      );
    });

    updates.push({ type: "location", action: "add", records: locs });
    group.update(updates);
    searchResults = [];
  }

  function clearResults() {
    allResults = [];
    searchResults = [];
    searchText = "";
  }

  function setImportAs(value) {
    importSearchResultsAs = value;
    showImportAs = false;
  }

  function zoomFitResults() {
    if (searchResults.length) {
      const points = searchResults
        .map((r) => {
          const index = visibleIndices[r.page_id];
          return {
            index,
            point: r.center,
          };
        })
        .filter((p) => p.index != undefined);
      if (points.length) {
        dispatch("zoom-to-bounds", { points });
      }
    } else if (filtered.length) {
      const points = filtered
        .map((r) => {
          const center = resultCenterPoint(r);
          const index = visibleIndices[r.page_id];
          return {
            index,
            point: center,
          };
        })
        .filter((p) => p.index != undefined);
      if (points.length) {
        dispatch("zoom-to-bounds", { points });
      }
    }
  }

  function isolateResults() {
    if (searchResults.length) {
      const pages = uniq(searchResults.map((r) => r.page_id));
      dispatch("isolate-pages", { pages });
    } else if (filtered.length) {
      const pages = uniq(filtered.map((r) => r.page_id));
      dispatch("isolate-pages", { pages });
    }
  }

  onMount(() => {
    eb.on("open-search", toggleVisible);
    eb.on("close-doc-panels", closePanel);

    return () => {
      eb.unsubscribe("open-search", toggleVisible);
      eb.unsubscribe("close-doc-panels", closePanel);
    };
  });
</script>

{#if visible}
  <div class="w-full max-h-full flex justify-end p-4 absolute z-30 pointer-events-none text-xs">
    <div class="w-72 pointer-events-auto max-h-full flex flex-col">
      <div class="flex-none bg-white p-2 rounded border shadow-lg w-full">
        <div class="flex items-center gap-0.5">
          <div class="grow flex rounded border relative">
            <input
              size="1"
              bind:this={input}
              bind:value={searchText}
              on:input={handleInput}
              on:keydown={keydown} />
            {#if searchType === "text" && searchText}
              <button
                class="absolute right-0 top-0 p-1 text-gray-400 hover:text-black"
                on:click={clearResults}>
                <CircleXIcon />
              </button>
            {:else if searchType === "location" && filtered.length}
              <div class="w-full h-full absolute flex justify-between items-center">
                <div class="italic text-gray-500 px-1">&lt;Item Search&gt;</div>
                <button class="text-gray-400 hover:text-black p-1" on:click={clearResults}>
                  <CircleXIcon />
                </button>
              </div>
            {/if}
          </div>
          <button
            use:tooltip={{ text: "Use wildcards" }}
            class="rounded p-1 hover:bg-gray-200"
            class:text-gray-400={!$useWildcardsInSearch}
            class:bg-transparent={!$useWildcardsInSearch}
            class:bg-gray-200={$useWildcardsInSearch}
            on:click={() => ($useWildcardsInSearch = !$useWildcardsInSearch)}>
            <QuestionAsteriskIcon />
          </button>
          <button
            use:tooltip={{ text: "Search Selected Pages" }}
            class="rounded p-1 hover:bg-gray-200"
            class:text-gray-400={!$useSelectedInSearch}
            class:bg-transparent={!$useSelectedInSearch}
            class:bg-gray-200={$useSelectedInSearch}
            on:click={() => ($useSelectedInSearch = !$useSelectedInSearch)}>
            <SelectedIcon />
          </button>
          <div class="border-l border-gray-200">&#8288;</div>
          <button on:click={toggleVisible} class="rounded p-1 hover:bg-gray-200">
            <XICon />
          </button>
        </div>
        <div class="text-xs flex justify-between items-center mt-2">
          <div class="flex gap-2 items-center">
            {#if canShowResults}
              {#if filtered.length && searchResults.length === filtered.length}
                <button class="btn-text" on:click={hidePreview}>Hide Markers</button>
              {:else}
                <button class="btn-text" on:click={previewResults} disabled={!filtered.length}
                  >Show Markers</button>
              {/if}
            {:else}
              <button class="btn-text" disabled>Show Markers</button>
            {/if}
            {#if canShowResults && (searchResults.length || filtered.length)}
              <div class="flex items-center">
                <button
                  class="rounded p-1 hover:bg-gray-200"
                  on:click={zoomFitResults}
                  use:tooltip={{ text: "Zoom to Results" }}>
                  <ZoomFitIcon />
                </button>
                <button
                  class="rounded p-1 hover:bg-gray-200"
                  on:click={isolateResults}
                  use:tooltip={{ text: "Isolate Results" }}>
                  <IsolateIcon />
                </button>
              </div>
            {:else}
              <div class="p-1">
                <BlankIcon />
              </div>
            {/if}
          </div>
          {#if searchResults.length}
            <div class="flex gap-1">
              <button class="btn-text" on:click={importResults}>
                Import as {upperFirst(recordName[importSearchResultsAs])}
              </button>
              <button
                on:click={() => (showImportAs = !showImportAs)}
                class="rounded p-1 hover:bg-gray-200 relative">
                <CaretDownIcon />
                {#if showImportAs}
                  <div class="dropdown-container text-right right-0">
                    <button class="dropdown-button-item" on:click|stopPropagation={() => setImportAs("item")}
                      >Item</button>
                    <button
                      class="dropdown-button-item"
                      on:click|stopPropagation={() => setImportAs("collection")}>Opening</button>
                    <button class="dropdown-button-item" on:click|stopPropagation={() => setImportAs("type")}
                      >Type</button>
                    <button
                      class="dropdown-button-item"
                      on:click|stopPropagation={() => setImportAs("document_note")}>Note</button>
                  </div>
                {/if}
              </button>
            </div>
          {/if}
        </div>
      </div>
      <div class="grow bg-white rounded border shadow-lg w-full max-h-full overflow-hidden flex flex-col">
        {#if !canShowResults}
          <div class="p-2">
            <div class="flex-none flex justify-between">
              {#if !searching && (!searchText || searchText !== sentSearchText)}
                <button disabled={!searchText} on:click={() => chooseSearchText(searchText)} class="p-1">
                  <SearchIcon />
                </button>
                <div class="flex items-center gap-1">
                  <button class="btn-text" on:click={searchForLocations}>
                    Search for {upperFirst(recordName[searchForLocationsOf])}s ({selectedSearchedRecords.length})
                  </button>
                  <button on:click={toggleItemSearch} class="rounded p-1 hover:bg-gray-200">
                    <EllipsisIcon />
                  </button>
                </div>
              {:else}
                <div class="relative">
                  {#if searching}
                    <div class="animate-spin w-4 h-4">
                      <SpinnerIcon />
                    </div>
                  {:else}
                    {filtered.length} result{filtered.length === 1 ? "" : "s"}
                  {/if}
                </div>
              {/if}
            </div>
            {#if searchText && hiddenResults.length && !$useSelectedInSearch}
              <div class="flex-none flex items-center justify-end text-gray-500 italic">
                <div>
                  {hiddenResults.length} hidden
                </div>
              </div>
            {/if}
          </div>
        {:else}
          <div class="flex-none flex items-center justify-between p-2">
            <div>
              {currentResult + 1} / {filtered.length}
            </div>
            <div>
              Page {visibleIndices[current.page_id] + 1}
            </div>
            <div class="flex gap-1">
              <button on:click={prevResult} class="rounded p-1 hover:bg-gray-200">
                <CaretLeftIcon />
              </button>
              <button on:click={nextResult} class="rounded p-1 hover:bg-gray-200">
                <CaretRightIcon />
              </button>
            </div>
          </div>
        {/if}
        {#if showLocationPanel && !searching && !canShowResults && !searchText}
          <div class="flex-none px-3 flex justify-between items-center border-b">
            <div class="flex gap-2">
              <button
                class="record-type-toggle"
                class:active={searchForLocationsOf === "item"}
                on:click={() => (searchForLocationsOf = "item")}>Items</button>
              <button
                class="record-type-toggle"
                class:active={searchForLocationsOf === "collection"}
                on:click={() => (searchForLocationsOf = "collection")}>Openings</button>
              <button
                class="record-type-toggle"
                class:active={searchForLocationsOf === "type"}
                on:click={() => (searchForLocationsOf = "type")}>Types</button>
              <button
                class="record-type-toggle"
                class:active={searchForLocationsOf === "document_note"}
                on:click={() => (searchForLocationsOf = "document_note")}>Notes</button>
            </div>
            <div class="flex items-center gap-2">
              <button on:click={invertRecordSelection}>
                <InvertIcon />
              </button>
              <input
                bind:this={recordSelector}
                type="checkbox"
                bind:checked={overallRecordSelection}
                on:input={selectAllRecords} />
            </div>
          </div>
          <div class="p-2 grow overflow-y-auto">
            <div class="px-1">
              {#each recordsToSearch as record}
                <div class="flex justify-between items-center">
                  <div class="grow truncate">{record.mark}</div>
                  <div class="flex-none">
                    <input
                      type="checkbox"
                      class="mt-1"
                      bind:checked={selectedRecords[record.id]}
                      on:change={(e) => {
                        initialToggle = e.target.checked;
                      }}
                      on:click|preventDefault
                      on:mousedown={(e) => {
                        selectedRecords[record.id] = !selectedRecords[record.id];
                        initialToggle = selectedRecords[record.id];
                      }}
                      on:mouseenter={(e) => {
                        if (e.buttons === 1) {
                          selectedRecords[record.id] = initialToggle;
                        }
                      }} />
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  input {
    @apply grow p-1 bg-transparent;
    // outline: none;
    min-width: 0;
    max-width: 100%;
  }

  button.record-type-toggle {
    @apply text-gray-500 border-b border-transparent py-1;

    &.active {
      @apply text-black border-black;
    }
  }
</style>
